/**
 * Lightweight hook for unread message badge count.
 * Aggregates trainer_unread_count from conversations table
 * with real-time subscription for instant updates.
 */

import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Returns the total unread message count for the authenticated trainer.
 * Subscribes to real-time changes on conversations for instant badge updates.
 */
export function useUnreadMessageCount() {
  const queryClient = useQueryClient();

  const { data: totalUnread = 0 } = useQuery({
    queryKey: ['unread-message-count'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return 0;

      const { data, error } = await supabase
        .from('conversations')
        .select('trainer_unread_count')
        .eq('trainer_id', session.user.id);

      if (error) {
        console.error('Failed to fetch unread count:', error);
        return 0;
      }

      return (data || []).reduce(
        (sum, conv) => sum + (conv.trainer_unread_count ?? 0),
        0
      );
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  // Real-time subscription for conversation unread count changes
  useEffect(() => {
    const channel = supabase
      .channel('unread-message-count')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'conversations' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['unread-message-count'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  return { totalUnread };
}
